import { http } from '@/api/http'

/** How the server lays out a column: numbers right-aligned and summed by Excel, dates as dates. */
export type ExportColumnKind = 'text' | 'number' | 'date'
export type ExportCell = string | number | null

/** A row's part in the table: a group heading («Говядина»), a subtotal or the «Итого» row. */
export type ExportRowKind = 'data' | 'section' | 'subtotal' | 'total'

/** One line of the header block: «Среда: Main test», «Склады: Все». */
export type ExportLine = { label: string; value: string }

/**
 * A table as the screen shows it, ready for «Печать», the browser, a PDF or
 * Excel. Every one of them shows the same sheet (client, ALFI TEST 306): the
 * title, the header block (Среда, Отчёт создан, Исполнитель, then the
 * filters), the columns and the rows with their group and «Итого» rows.
 */
export type ExportTable = {
  /** The file's name, without the date or extension. */
  name: string
  /** The heading of every output. */
  title: string
  /** A free line under the title (a document's number, a party). */
  subtitle?: string
  /** The screen's active filters as shown, «Все» when one is empty. */
  filters?: ExportLine[]
  /** The whole header block, when the server laid the sheet out itself. */
  lines?: ExportLine[]
  columns: { title: string; kind: ExportColumnKind | 'datetime' }[]
  rows: ExportCell[][]
  /** Each row's part; a row left out is a data row. */
  rowKinds?: ExportRowKind[]
}

export type ExportFormat = 'print' | 'browser' | 'pdf' | 'xlsx'

/** Who exports and where: it heads every output. */
export type ExportHeader = {
  /** The server's label language: en, ru, uz or uz_cyrl. */
  lang: string
  company: string
  author: string
}

/**
 * Asks the server to put the header block above a client-form Excel file
 * (Акт сверки, табель…): the X-Export-Header request header, base64 of
 * {lang, company, author, title, filters}. `title` heads a form without one.
 */
export function exportHeaderRequest(header: ExportHeader, filters: ExportLine[], title?: string): Record<string, string> {
  const cut = (text: string) => (text.length > 300 ? `${text.slice(0, 299)}…` : text)
  const json = JSON.stringify({
    lang: header.lang,
    company: header.company,
    author: header.author,
    title: title ?? '',
    filters: filters.map(({ label, value }) => ({ label: cut(label), value: cut(value) })),
  })
  let binary = ''
  for (const byte of new TextEncoder().encode(json)) binary += String.fromCharCode(byte)
  return { 'X-Export-Header': btoa(binary) }
}

/** The header block's labels, in the interface language. */
export type ExportLabels = { company: string; created: string; author: string; all: string; print: string }

const pad = (part: number) => String(part).padStart(2, '0')

/** «25.10.2026 15:04», the server's «Отчёт создан» format. */
export const exportStamp = (at = new Date()) =>
  `${pad(at.getDate())}.${pad(at.getMonth() + 1)}.${at.getFullYear()} ${pad(at.getHours())}:${pad(at.getMinutes())}`

/**
 * The header block as the server writes it into the PDF and Excel files:
 * Среда, Отчёт создан, Исполнитель, then each filter («Все» when empty).
 * A sheet the server laid out brings its own lines.
 */
export function headerLines(table: ExportTable, header: ExportHeader, labels: ExportLabels, at = new Date()): ExportLine[] {
  if (table.lines) return table.lines
  const lines: ExportLine[] = []
  if (header.company.trim()) lines.push({ label: labels.company, value: header.company.trim() })
  lines.push({ label: labels.created, value: exportStamp(at) })
  if (header.author.trim()) lines.push({ label: labels.author, value: header.author.trim() })
  for (const filter of table.filters ?? []) {
    const label = filter.label.trim().replace(/:$/, '')
    if (label) lines.push({ label, value: filter.value.trim() || labels.all })
  }
  return lines
}

/** A report sheet the server laid out (format=sheet): the very sheet its PDF and Excel print. */
export type ExportSheetDto = {
  name: string
  title: string
  subtitle?: string
  lines: ExportLine[]
  columns: { title: string; kind: 'text' | 'number' | 'date' | 'datetime' }[]
  rows: { kind: ExportRowKind; cells: ExportCell[] }[]
}

/** The server's sheet as a table for «Печать» and «Открыть в браузере». */
export const sheetTable = (sheet: ExportSheetDto): ExportTable => ({
  name: sheet.name,
  title: sheet.title,
  subtitle: sheet.subtitle,
  lines: sheet.lines ?? [],
  columns: (sheet.columns ?? []).map((column) => ({ title: column.title, kind: column.kind })),
  rows: (sheet.rows ?? []).map((row) => row.cells ?? []),
  rowKinds: (sheet.rows ?? []).map((row) => row.kind),
})

const TOTAL = /^(итого|всего|total|grand total|jami|жами|umumiy|умумий)(?=$|[\s:.,(])/i

const DATE = /^\d{2}\.\d{2}\.\d{4}( \d{2}:\d{2})?$/
const NUMBER = /^[-−+]?[\d\s  ]*\d(?:[.,]\d+)?\s?%?$/
const SPACES = /[\s  ]+/g

/** The text a cell shows, on one line. */
export const cleanText = (value: string | null | undefined) => (value ?? '').replace(SPACES, ' ').trim()

/** A shown «28 000,5» as 28000.5; text that is not a number stays as it is. */
export function shownNumber(value: string): number | string {
  if (!NUMBER.test(value) || value.endsWith('%')) return value
  const number = Number(value.replace(SPACES, '').replace('−', '-').replace(',', '.'))
  return Number.isFinite(number) ? number : value
}

/**
 * Reads the table a screen shows: its header (a grouped header reads
 * «Group / Column») and its rows, a cell spanning several columns written in
 * the first. A column whose cells are all dates is a date column; a column
 * of numbers, or one the screen aligns right, is a number column.
 */
export function readTable(table: HTMLTableElement, name: string, title: string, subtitle?: string): ExportTable {
  // A heading spanning several columns heads each of them; a body cell's text goes in its first column.
  const grid = (rows: HTMLTableRowElement[], repeat: boolean) => {
    const cells: { text: string; right: boolean }[][] = []
    const taken: boolean[][] = []
    rows.forEach((row, rowIndex) => {
      cells[rowIndex] ??= []
      taken[rowIndex] ??= []
      let column = 0
      for (const cell of [...row.cells]) {
        while (taken[rowIndex][column]) column++
        const span = Math.max(1, cell.colSpan || 1)
        const down = Math.max(1, cell.rowSpan || 1)
        const text = cellText(cell)
        const right = cell.classList.contains('text-right') || cell.classList.contains('num') || getComputedStyle(cell).textAlign === 'right'
        for (let r = 0; r < down; r++)
          for (let c = 0; c < span; c++) {
            cells[rowIndex + r] ??= []
            taken[rowIndex + r] ??= []
            taken[rowIndex + r][column + c] = true
            cells[rowIndex + r][column + c] = { text: repeat || (r === 0 && c === 0) ? text : '', right }
          }
        column += span
      }
    })
    return cells
  }
  const visible = (row: HTMLTableRowElement) => row.offsetParent !== null || row.getClientRects().length > 0 || !row.isConnected
  const headRows = table.tHead ? [...table.tHead.rows].filter(visible) : []
  const bodyRows = [...table.tBodies].flatMap((body) => [...body.rows]).concat(table.tFoot ? [...table.tFoot.rows] : []).filter(visible)
  const head = grid(headRows, true)
  const footFrom = bodyRows.length - (table.tFoot ? [...table.tFoot.rows].filter(visible).length : 0)
  const filled = grid(bodyRows, false).map((cells, index) => ({ cells, index })).filter(({ cells }) => cells.some((cell) => cell?.text))
  const body = filled.map(({ cells }) => cells)
  const width = Math.max(0, ...head.map((row) => row.length), ...body.map((row) => row.length))
  const titles = Array.from({ length: width }, (_, column) => {
    const parts: string[] = []
    for (const row of head) {
      const text = row[column]?.text
      if (text && parts[parts.length - 1] !== text) parts.push(text)
    }
    return parts.join(' / ')
  })
  // Columns with neither a heading nor a value (checkboxes, row actions) are left out.
  const keep = titles.map((titleText, column) => !!titleText || body.some((row) => row[column]?.text))
  const columns = titles
    .map((titleText, column) => {
      const values = body.map((row) => row[column]?.text ?? '').filter((text) => text && text !== '—')
      const kind: ExportColumnKind =
        values.length && values.every((text) => DATE.test(text))
          ? 'date'
          : values.length && values.every((text) => typeof shownNumber(text) === 'number')
            ? 'number'
            : !values.length && body.some((row) => row[column]?.right)
              ? 'number'
              : 'text'
      return { title: titleText, kind }
    })
    .filter((_, column) => keep[column])
  const rows = body.map((row) =>
    Array.from({ length: width }, (_, column) => row[column]?.text ?? '')
      .filter((_, column) => keep[column])
      .map((text, index): ExportCell => {
        if (!text || text === '—') return null
        return columns[index].kind === 'number' ? shownNumber(text) : text
      }),
  )
  // The «Итого» rows (a footer, or a row that starts with it) and the group headings (one cell across the row).
  const rowKinds = filled.map(({ cells, index }): ExportRowKind => {
    const texts = cells.filter((cell) => cell?.text).map((cell) => cell.text)
    if (index >= footFrom || TOTAL.test(texts[0] ?? '')) return 'total'
    const row = bodyRows[index]
    const lone = row.cells.length === 1 || (texts.length === 1 && [...row.cells].some((cell) => cell.colSpan > 1))
    return lone && width > 2 ? 'section' : 'data'
  })
  return { name, title, subtitle, columns, rows, rowKinds: rowKinds.some((kind) => kind !== 'data') ? rowKinds : undefined }
}

/** A cell's text as shown; a switch or a checkbox reads as its state. */
function cellText(cell: Element) {
  const clone = cell.cloneNode(true) as Element
  clone.querySelectorAll('svg, [aria-hidden="true"], .sr-only').forEach((node) => node.remove())
  const control = cell.querySelector('[role="switch"], input[type="checkbox"]')
  const text = cleanText(clone.textContent)
  if (control && !text) {
    const on = control.getAttribute('aria-checked') === 'true' || (control as HTMLInputElement).checked
    return on ? '✓' : ''
  }
  const input = cell.querySelector<HTMLInputElement>('input:not([type="checkbox"]), textarea, select')
  if (!text && input) return cleanText(input.value)
  return text
}

/**
 * A document's own fields as its form shows them («Дата: 25.10.2026»,
 * «Склад: База»), for the header block: every labelled control outside the
 * document's tables that holds a value.
 */
export function readFields(scope: HTMLElement | null): ExportLine[] {
  if (!scope) return []
  const lines: ExportLine[] = []
  for (const label of scope.querySelectorAll<HTMLLabelElement>('label[for]')) {
    if (label.closest('[data-export-block], table')) continue
    const control = label.htmlFor ? scope.ownerDocument.getElementById(label.htmlFor) : null
    if (!control || control.closest('[data-export-block], table')) continue
    let value = ''
    if (control instanceof HTMLSelectElement) value = control.selectedOptions[0]?.textContent ?? ''
    else if (control instanceof HTMLInputElement) value = control.type === 'checkbox' ? (control.checked ? '✓' : '') : control.value
    else if (control instanceof HTMLTextAreaElement) value = control.value
    else value = cellText(control)
    const text = cleanText(label.textContent).replace(/\*$/, '').trim()
    value = cleanText(value)
    if (text && value && !lines.some((line) => line.label === text)) lines.push({ label: text, value })
  }
  return lines.slice(0, 30)
}

/** The table shown inside an element; the widest one when there are several. */
export function findTable(root: Element | null): HTMLTableElement | null {
  if (!root) return null
  if (root instanceof HTMLTableElement) return root
  const tables = [...root.querySelectorAll('table')]
  return tables.sort((a, b) => b.rows.length * (b.rows[0]?.cells.length ?? 0) - a.rows.length * (a.rows[0]?.cells.length ?? 0))[0] ?? null
}

const today = () => new Date().toISOString().slice(0, 10)

/** Saves a file the browser received. */
export function saveFile(blob: Blob, fileName: string) {
  const href = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = href
  anchor.download = fileName
  anchor.click()
  window.setTimeout(() => URL.revokeObjectURL(href), 1000)
}

/**
 * The table as an .xlsx or a .pdf file made by the server (/exports/table),
 * downloaded: the same header block, columns and rows as the browser view.
 */
export async function downloadTable(table: ExportTable, format: 'pdf' | 'xlsx', header: ExportHeader) {
  const { data } = await http.post<Blob>(
    '/exports/table',
    {
      file_name: table.name,
      format,
      title: table.title,
      subtitle: table.subtitle ?? '',
      lang: header.lang,
      company: header.company,
      author: header.author,
      filters: (table.filters ?? []).map(({ label, value }) => ({ label, value })),
      columns: table.columns.map(({ title, kind }) => ({ title, kind: kind === 'datetime' ? 'date' : kind })),
      rows: table.rows,
      row_kinds: table.rowKinds,
    },
    { responseType: 'blob' },
  )
  saveFile(data, `${table.name}-${today()}.${format}`)
}

const escape = (value: unknown) =>
  String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] as string)

/** A number as the screen and the server's PDF write it: spaces between thousands, a point before at most four decimals. */
const shown = (value: ExportCell) => {
  if (typeof value !== 'number') return value ?? ''
  const [whole, fraction] = String(Number(value.toFixed(4))).split('.')
  const sign = whole.startsWith('-') ? '-' : ''
  const digits = sign ? whole.slice(1) : whole
  return sign + digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + (fraction ? `.${fraction}` : '')
}

/**
 * The table as a page of its own, to read, print or save from the browser:
 * the same sheet as the PDF — Arial, the title, the header block on light
 * grey, the table with its light blue header and its group and «Итого» rows
 * on light grey — on an A4 landscape page with the header
 * repeated and «N / M» at the foot. `print` opens the print dialog at once.
 */
export function tableDocument(table: ExportTable, lines: ExportLine[], labels: { print: string }, print = false) {
  const head = table.columns.map((column) => `<th class="${column.kind}">${escape(column.title)}</th>`).join('')
  const body = table.rows
    .map((row, rowIndex) => {
      const kind = table.rowKinds?.[rowIndex] ?? 'data'
      return `<tr class="${kind}">${table.columns.map((column, index) => `<td class="${column.kind}">${escape(shown(row[index] ?? null))}</td>`).join('')}</tr>`
    })
    .join('')
  const block = lines.map((line) => `<div><b>${escape(line.label)}:</b><span>${escape(line.value)}</span></div>`).join('')
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escape(table.title)}</title><style>
@page{size:A4 landscape;margin:10mm;@bottom-center{content:counter(page) " / " counter(pages);font-size:8pt;color:#787878}}
body{font-family:Arial,"Liberation Sans",Helvetica,sans-serif;margin:24px;color:#101828}
header{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:12px}
h1{font-size:18px;margin:0 0 6px}.subtitle{margin:0 0 4px;font-size:12.5px}
.lines{display:inline-grid;grid-template-columns:auto auto;background:#f2f2f2;font-size:12px;line-height:1.5;padding:4px 0;margin-top:4px}.lines div{display:contents}.lines b{font-weight:700;padding:0 10px}.lines span{padding:0 16px 0 0}
button{font-family:inherit;font-size:13px;padding:6px 14px;border:1px solid #d0d5dd;border-radius:8px;background:#fff;cursor:pointer}
table{border-collapse:collapse;width:100%;font-size:12px}
th,td{border:1px solid #d0d5dd;padding:4px 7px;text-align:left;vertical-align:top}
td.text{white-space:pre-wrap}
th{background:#ddebf7;color:#101828;font-weight:700;text-align:center;position:sticky;top:0}
td.number{text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums}
td.date,td.datetime{white-space:nowrap}
tr.section td,tr.subtotal td,tr.total td{background:#f2f2f2;font-weight:700}
thead{display:table-header-group}tr{break-inside:avoid}
@media print{button{display:none}body{margin:0}th{position:static;-webkit-print-color-adjust:exact;print-color-adjust:exact}td,.lines{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body><header><div><h1>${escape(table.title)}</h1>${table.subtitle ? `<p class="subtitle">${escape(table.subtitle)}</p>` : ''}<div class="lines">${block}</div></div>
<button onclick="window.print()">${escape(labels.print)}</button></header>
<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>${print ? '<script>window.addEventListener("load",function(){setTimeout(function(){window.print()},150)})</script>' : ''}</body></html>`
}

/**
 * Shows the table in a browser tab («Открыть в браузере»), or opens it for
 * printing («Печать»). The tab is opened on the click (a tab opened later is
 * blocked as a pop-up) and filled when the table is ready; pass it in from
 * the click.
 */
export function showTable(tab: Window | null, table: ExportTable, lines: ExportLine[], labels: { print: string }, print = false) {
  const href = URL.createObjectURL(new Blob([tableDocument(table, lines, labels, print)], { type: 'text/html;charset=utf-8' }))
  if (tab && !tab.closed) tab.location.href = href
  else window.open(href, '_blank')
  window.setTimeout(() => URL.revokeObjectURL(href), 60_000)
}
